import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api-client.js';

export function ResetPasswordPage(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError('Missing reset token in the URL');
  }, [token]);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/v1/auth/password-reset-complete', {
        token,
        new_password: newPassword,
      });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError('That reset link is invalid or expired. Request a new one.');
      } else {
        setError(err instanceof Error ? err.message : 'Reset failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-md space-y-4">
        <h1 className="text-2xl font-semibold">Password updated</h1>
        <p className="text-sm text-ink-muted">Redirecting you to sign in…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md">
      <h1 className="text-2xl font-semibold mb-4">Choose a new password</h1>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label htmlFor="np" className="block text-sm font-medium mb-1">New password</label>
          <input
            id="np"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => { setNewPassword(e.target.value); setError(null); }}
            className="w-full rounded border border-border bg-surface text-ink p-2 focus:outline-none focus:ring-2 focus:ring-accent"
            autoFocus
          />
          <p className="text-xs text-ink-muted mt-1">At least 8 characters.</p>
        </div>
        <div>
          <label htmlFor="cp" className="block text-sm font-medium mb-1">Confirm new password</label>
          <input
            id="cp"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => { setConfirm(e.target.value); setError(null); }}
            className="w-full rounded border border-border bg-surface text-ink p-2 focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
        <button
          type="submit"
          disabled={submitting || !token}
          className="w-full rounded bg-accent text-accent-fg px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60"
        >
          {submitting ? 'Updating…' : 'Update password'}
        </button>
        <Link to="/login" className="block text-xs text-ink-muted hover:text-ink">
          Back to sign in
        </Link>
      </form>
    </div>
  );
}
